// Shared plan-status rules for a CustomerBalance, used by both CustomerProfilePage
// (to pick which prompt/pill to show) and ActivePackageCard (to render the usage
// panel and severity colors). Keeping this in one place avoids the two views
// silently drifting out of sync on what "expired" / "exhausted" actually means.
import {CustomerBalance} from "@/interfaces/entities/CustomerProfile";
import {toBytes} from "@/util/dataSize";

export function isUnlimitedBalance(balance: CustomerBalance | undefined): boolean {
    return balance?.balance.extendedDataPolicy?.type === "unlimited";
}

// A credits balance reuses size/availableBalance, denominated in the
// purchase currency (sizeUnit === price.currencyCode) instead of a data unit.
export function isCreditsBasedBalance(balance: CustomerBalance | undefined): boolean {
    if (!balance) return false;
    return !!balance.balance.size.sizeUnit && balance.balance.size.sizeUnit === balance.price.currencyCode;
}

// Share of the package still available. `size` and `availableBalance` can arrive in different
// units (see util/dataSize), so normalise to bytes before dividing; a non-data balance such as
// a credits wallet has no byte equivalent and its two values are already comparable as-is.
export function dataRemainingPercent(balance: CustomerBalance | undefined): number {
    if (!balance) return 0;
    const totalBytes = toBytes(balance.balance.size);
    const remainingBytes = toBytes(balance.balance.availableBalance);
    if (totalBytes !== null && remainingBytes !== null) {
        return totalBytes > 0 ? (remainingBytes / totalBytes) * 100 : 0;
    }
    const total = balance.balance.size.sizeValue;
    return total > 0 ? (balance.balance.availableBalance.sizeValue / total) * 100 : 0;
}

export function isDataExhaustedBalance(balance: CustomerBalance | undefined): boolean {
    if (!balance) return false;
    const packageSize = balance.balance.size.sizeValue;
    const remainingGb = balance.balance.availableBalance.sizeValue;
    return !isUnlimitedBalance(balance) && packageSize > 0 && remainingGb <= 0;
}

// Before activation, track elapsed time since purchase instead of activation.
export function computeValidityElapsedPercent(balance: CustomerBalance | undefined): number {
    if (!balance) return 0;
    const anchor = balance.balance.activatedAt ?? balance.salesDate;
    if (!anchor) return 0;
    const start = new Date(anchor).getTime();
    const expires = new Date(balance.balance.expiresAt).getTime();
    const total = expires - start;
    if (total <= 0) return 100;
    const elapsed = Date.now() - start;
    return Math.max(0, Math.min(100, (elapsed / total) * 100));
}

export function isValidityExpiredBalance(balance: CustomerBalance | undefined): boolean {
    return !isCreditsBasedBalance(balance) && computeValidityElapsedPercent(balance) >= 100;
}

/**
 * Which of an eSIM's balances the profile page should present as the current one.
 *
 * The API returns expired balances alongside live ones, and an expired balance still carries an
 * `activatedAt` - so picking "the activated one" would show a customer with a perfectly good plan
 * the expired card instead. Live balances win; an expired one is only shown when there is nothing
 * else, and then the most recently expired, so the page explains itself rather than looking empty.
 */
export function selectActiveBalance(domainBalances: Array<CustomerBalance>): CustomerBalance | undefined {
    const live = domainBalances.filter((b: CustomerBalance) => !isValidityExpiredBalance(b));
    const usable = live.filter((b: CustomerBalance) => !isDataExhaustedBalance(b));
    // An eSIM can carry several balances queued in sequence - an activated, usable one is current.
    // Once every activated package is exhausted, promote the first usable queued package instead.
    const current = usable.find((b: CustomerBalance) => b.balance.activatedAt) ?? usable[0];
    if (current) return current;

    // Keep an exhausted/expired package visible when there is no usable one, so the page can
    // explain why there is no data rather than looking as though the customer never had a plan.
    return live[0] ?? domainBalances
        .slice()
        .sort((a: CustomerBalance, b: CustomerBalance) =>
            new Date(b.balance.expiresAt).getTime() - new Date(a.balance.expiresAt).getTime())[0];
}

/** The rest of the usable queue. Expired or exhausted balances are history, never "up next". */
export function selectQueuedBalances(
    domainBalances: Array<CustomerBalance>,
    activeBalance: CustomerBalance | undefined
): Array<CustomerBalance> {
    return domainBalances.filter(
        (b: CustomerBalance) => b !== activeBalance
            && !isValidityExpiredBalance(b)
            && !isDataExhaustedBalance(b)
    );
}

/** Previous package history, excluding a package already shown as the main fallback. */
export function selectPreviousBalances(
    domainBalances: Array<CustomerBalance>,
    activeBalance: CustomerBalance | undefined
): Array<CustomerBalance> {
    return domainBalances.filter(
        (b: CustomerBalance) => b !== activeBalance
            && (isDataExhaustedBalance(b) || isValidityExpiredBalance(b))
    );
}
